import { area, bbox, bboxPolygon } from '@turf/turf';
import type { MultiPolygonG2D } from '@ev-tech/search-modules-api';

import { MULTIPOLYGON_COMPARE_DECIMALS } from './const';

/** Area delta (m²) allowed when comparing polygon area to its envelope rectangle. */
const BBOX_AREA_TOLERANCE_SQM = 1;

/**
 * True when the multipolygon is a single axis-aligned rectangle (map viewport / legacy bounding box search).
 * Uses Turf: if the shape fills its own axis-aligned bounding box, it is a rectangle.
 */
export const isMultiPolygonBoundingBox = (multiPolygon: MultiPolygonG2D): boolean => {
  const { coordinates } = multiPolygon;
  if (coordinates.length !== 1) {
    return false;
  }

  const polygon = coordinates[0];
  if (polygon?.length !== 1) {
    return false;
  }

  const geometry = { type: 'MultiPolygon' as const, coordinates };
  const bboxRectangle = bboxPolygon(bbox(geometry));

  return Math.abs(area(geometry) - area(bboxRectangle)) < BBOX_AREA_TOLERANCE_SQM;
};

const roundLngLatComponent = (n: number): number => Number(n.toFixed(MULTIPOLYGON_COMPARE_DECIMALS));

/** Stable string for comparing MultiPolygon coordinates (tolerant floating-point noise). */
export const normalizeMultiPolygonCoordinatesForCompare = (coordinates?: number[][][][]): string => {
  if (!coordinates?.length) {
    return '[]';
  }

  return JSON.stringify(
    coordinates.map((polygon) =>
      polygon.map((ring) =>
        ring.map((pos) => {
          const lng = Number(pos[0]);
          const lat = Number(pos[1]);
          return [roundLngLatComponent(lng), roundLngLatComponent(lat)];
        }),
      ),
    ),
  );
};
